import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Pane, PaneHead, ToggleRow } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { money } from '../lib/scope';
import { useWallet, walletRunwayWeeks } from '../lib/useManagerData';

/**
 * The Auto top-up door (g_wauto / g_w3), docked on the wallet hub.
 *
 *   GET /me/wallet              balance + auto-reload config
 *   PUT /me/wallet/auto-reload  enabled / threshold / amount  (billing:write)
 *
 * Auto-reload is Stripe-only and inert without a saved card, so the switch is
 * refused with the reason instead of being offered and ignored. The board's
 * "tell me when" alerts have no preference endpoint: the owner is always
 * emailed (low balance at most once a day, and every failed reload), so the
 * switches are drawn on and locked rather than offered and ignored. The cap
 * note quotes the platform ceilings the server clamps to.
 */
const PLATFORM_MAX_RELOAD = 2000;
const PLATFORM_MAX_MONTHLY = 10000;
export function AutoTopUpPane({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const { wallet } = useWallet();
  const ar = wallet?.autoReload;
  const currency = wallet?.currency?.toUpperCase() ?? 'EGP';
  const canSwitchOn = !!ar?.supported && !!ar?.cardOnFile;

  const [on, setOn] = useState<boolean | null>(null);
  const [threshold, setThreshold] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enabled = on ?? ar?.enabled ?? false;
  const thresholdV = threshold ?? (ar?.thresholdMinor != null ? String(ar.thresholdMinor / 100) : '');
  const amountV = amount ?? (ar?.reloadAmountMinor != null ? String(ar.reloadAmountMinor / 100) : '');
  const weeks = walletRunwayWeeks(wallet);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { enabled };
      if (enabled) {
        const t = Number(thresholdV);
        const a = Number(amountV);
        if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(a) || a <= 0) {
          throw new Error('A threshold and a reload amount are both needed.');
        }
        body.thresholdMinor = Math.round(t * 100);
        body.reloadAmountMinor = Math.round(a * 100);
      }
      await apiClient.put('/me/wallet/auto-reload', body);
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['cm-wallet'] });
      onDone();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'That did not save.'),
  });

  const field = (label: string, value: string, set: (v: string) => void, hint?: string) => (
    <Card>
      <label>
        <span className="t-ovl mut">{label}</span>
        <input
          inputMode="decimal"
          value={value}
          onChange={(e) => set(e.target.value)}
          placeholder={currency}
          style={{ display: 'block', width: '100%', marginTop: 6, background: 'transparent',
            color: 'var(--ink-900)', border: '1px solid var(--stroke-200)', borderRadius: 'var(--r-sm)',
            padding: '10px 12px', fontFamily: 'inherit', fontSize: 'inherit', minHeight: 44 }}
        />
      </label>
      {hint ? <p className="t-cap mut mt4">{hint}</p> : null}
    </Card>
  );

  return (
    <Pane
      head={<PaneHead overline="The Auto top-up door" title="Auto top-up"
        sub="the setting that prevents the empty wallet" />}
      foot={(
        <button type="button" className="btn btn-primary" style={{ flex: 1 }}
          disabled={save.isPending || (enabled && !canSwitchOn)}
          onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      )}
    >
      <>
        <ToggleRow
          label="Top up automatically"
          checked={enabled}
          disabled={!enabled && !canSwitchOn}
          onChange={setOn}
          captionOn="so the messages never stop while nobody is looking"
          captionOff={canSwitchOn
            ? 'off — the wallet only moves when someone tops it up'
            : 'needs a card on file with a provider that can charge it off-session'}
        />
        {field('When the balance falls below', thresholdV, setThreshold,
          weeks !== null ? `about ${weeks} week${weeks === 1 ? '' : 's'} left at the current burn` : undefined)}
        {field('Add', amountV, setAmount)}
        <Card>
          <p className="t-ovl mut">From</p>
          <p className="t-sec sec mt4">
            {ar?.cardOnFile ? 'the card saved on this clinic’s account' : 'no card on file yet'}
          </p>
          <p className="t-cap mut">a card is saved under Subscription &amp; Usage</p>
        </Card>
        <Card kind="warn">
          <p className="t-sec">
            Never more than <span className="w6">{money(PLATFORM_MAX_RELOAD, currency)}</span> in one
            top-up, never more than <span className="w6">{money(PLATFORM_MAX_MONTHLY, currency)}</span> a
            month — two ceilings, so a loop cannot drain an account
          </p>
        </Card>
        <p className="t-ovl mut mt12">Tell me when</p>
        <ToggleRow
          label="The balance is low and can't top up by itself"
          checked
          disabled
          onChange={() => undefined}
          captionOn="at most one email a day"
          captionOff=""
        />
        <ToggleRow
          label="An automatic top-up fails"
          checked
          disabled
          onChange={() => undefined}
          captionOn="a failed card is why a wallet empties with auto on"
          captionOff=""
        />
        <p className="t-cap mut mt8">Alerts go to the clinic owner&rsquo;s email.</p>
        {ar?.thresholdMinor != null && ar.reloadAmountMinor != null ? (
          <p className="t-cap mut mt8">
            Now: {ar.enabled ? 'on' : 'off'} · adds {money(ar.reloadAmountMinor / 100, currency)} under{' '}
            {money(ar.thresholdMinor / 100, currency)}
          </p>
        ) : null}
        {error ? <p className="t-sec amb mt8">{error}</p> : null}
      </>
    </Pane>
  );
}
